import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { KycSteps } from "./KycSteps";
import { getContactDetails } from "../../api/account";
import {
  EMPTY_BUSINESS,
  businessProblems,
  confirmFee,
  getOnboarding,
  checkShortName,
  namespacePreview,
  shortNameProblem,
  saveBusiness,
  startFee,
  verifyDomain,
  type OnboardingBusiness,
  RESUME_NOTICE,
  BUSINESS_TYPES,
  businessNameLabel,
  personalBusiness,
} from "../../api/applicantOnboarding";
import { ContactDetailsCard } from "../account/ContactDetailsCard";
import { openRazorpayCheckout } from "../payments/testCheckout";

const rupees = (n: number) => `₹${n.toLocaleString("en-IN")}`;

function NamespaceName({ children }: { children: string }) {
  return (
    <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, fontFamily: "monospace" }}>
      {children}
    </Box>
  );
}

/**
 * A new operator's steps after signing up (owner 2026-10-30): verify email,
 * enter the business details and a short name, pay the non-refundable
 * Onboarding verification fee. The online KYC comes next. Hidden until Ankh
 * opens self-onboarding (`available`).
 */
export function OnboardingSteps() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const qc = useQueryClient();
  const key = ["applicant", "onboarding", ns];
  const q = useQuery({
    queryKey: key,
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: async () => (await getOnboarding(identity, ns)).data,
  });
  const contact = useQuery({
    queryKey: ["contactDetails", ns],
    enabled: Boolean(identity.token && ns && q.data?.available),
    retry: false,
    queryFn: () => getContactDetails(identity, ns),
  });
  const [form, setForm] = useState<OnboardingBusiness>(EMPTY_BUSINESS);
  const [problems, setProblems] = useState<string[]>([]);
  const [notice, setNotice] = useState("");
  const [nameCheck, setNameCheck] = useState<{ key: string; message: string } | null>(null);
  useEffect(() => {
    if (q.data) setForm(q.data.business);
  }, [q.data]);
  const set = (k: keyof OnboardingBusiness, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));
  const [hasDomain, setHasDomain] = useState(true);
  useEffect(() => {
    if (q.data?.namespace) setHasDomain(Boolean(q.data.business.domain));
  }, [q.data]);

  const verify = useMutation({
    mutationFn: async () => (await verifyDomain(identity, ns)).data,
    onSuccess: (d) => {
      qc.setQueryData(key, d);
      setNotice(d.domainverified ? "Domain proved." : "We can't see the record yet. It can take a few minutes; try again shortly.");
    },
    onError: (e: Error) => setNotice(e.message),
  });

  const save = useMutation({
    mutationFn: async () => (await saveBusiness(identity, ns, form)).data,
    onSuccess: (d) => {
      qc.setQueryData(key, d);
      setNotice("Details saved.");
    },
    onError: (e: Error) => setNotice(e.message),
  });

  const pay = useMutation({
    mutationFn: async () => {
      const started = (await startFee(identity, ns)).data;
      const c = started.fee.checkout;
      if (started.fee.status === "Paid" || !c) return started;
      const r = await openRazorpayCheckout({
        key: c.key,
        orderId: c.orderid,
        name: "Ankh Cloud",
        description: "Onboarding verification fee",
        prefill: { name: c.name, email: c.email, contact: c.contact },
      });
      if (!r) return started; // Closed: the order waits for the next press.
      return (await confirmFee(identity, ns, r.paymentId, r.signature)).data;
    },
    onSuccess: (d) => {
      qc.setQueryData(key, d);
      setNotice(d.fee.status === "Paid" ? "" : "The payment was not completed. You can try again.");
    },
    onError: (e: Error) => setNotice(e.message),
  });

  // The short name is checked as it is typed, so a taken one is marked at once.
  const typedShort = form.shortname.trim();
  const typedDomain = form.domain.trim();
  const nameKey = `${typedShort}|${typedDomain}`;
  useEffect(() => {
    if (!q.data?.editable || typedShort === "" || shortNameProblem(typedShort) !== "") return;
    let stale = false;
    const t = window.setTimeout(() => {
      checkShortName(identity, ns, typedShort, typedDomain)
        .then((r) => {
          if (!stale) setNameCheck({ key: nameKey, message: r.data.available ? "" : (r.data.message ?? "this short name is already taken") });
        })
        .catch(() => undefined); // The save checks again.
    }, 500);
    return () => {
      stale = true;
      window.clearTimeout(t);
    };
    // identity is a new object on every render; the token and namespace are what the answer depends on.
  }, [nameKey, q.data?.editable, identity.token, ns]);
  const nameProblem = nameCheck?.key === nameKey ? nameCheck.message : "";
  const nameFree = nameCheck?.key === nameKey && nameCheck.message === "";

  const data = q.data;
  if (!data?.available) return null;
  const paid = data.fee.status === "Paid";
  // Verification is free (owner 2026-10-30); the fee shows only if one is set or was paid.
  const noFee = !data.fee.amount && !data.fee.status;
  const emailVerified = contact.data?.emailVerified === true;
  const saved = Boolean(data.namespace);
  const dirty = JSON.stringify(form) !== JSON.stringify(data.business);
  const domainReady = !data.business.domain || data.domainverified;
  const verifyOpen = emailVerified && saved && !dirty && domainReady;
  const preview = namespacePreview(data.parent, hasDomain ? form.domain : "", form.shortname);
  const chooseDomain = (yes: boolean) => {
    setHasDomain(yes);
    setForm((f) => ({ ...f, domain: yes ? f.domain : "", declared: false }));
  };

  const onSave = () => {
    const p = businessProblems(form);
    setProblems(p);
    setNotice("");
    if (p.length === 0) save.mutate();
  };

  return (
    <Box sx={{ mt: 3 }} data-testid="onboarding-steps">
      <Typography variant="h6" gutterBottom>
        Finish setting up your account
      </Typography>
      <Typography variant="body2" color="text.secondary" data-testid="onboarding-resume-notice">
        {RESUME_NOTICE}
      </Typography>

      <Typography variant="subtitle1" sx={{ mt: 2 }}>
        Step 1: Verify your email {emailVerified && "✓"}
      </Typography>
      {!emailVerified && contact.isSuccess && <ContactDetailsCard />}

      <Typography variant="subtitle1" sx={{ mt: 3 }}>
        Step 2: Your business {saved && !dirty && "✓"}
      </Typography>
      {problems.length > 0 && (
        <Alert severity="warning" sx={{ my: 1 }}>
          {problems.map((p) => (
            <div key={p}>{p}</div>
          ))}
        </Alert>
      )}
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="body2" color="text.secondary">Business type</Typography>
          <RadioGroup row value={form.businesstype ?? ""} onChange={(e) => set("businesstype", e.target.value)} data-testid="business-type">
            {BUSINESS_TYPES.map((b) => (
              <FormControlLabel key={b.value} value={b.value} disabled={!data.editable} control={<Radio size="small" />} label={b.label} />
            ))}
          </RadioGroup>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField fullWidth size="small" required label={businessNameLabel(form.businesstype)} disabled={!data.editable} value={form.legalname}
            onChange={(e) => set("legalname", e.target.value)}
            helperText={personalBusiness(form.businesstype) ? (form.businesstype === "proprietorship" ? "Your firm's name. Your PAN will set the owner name." : "Your PAN will set this name.") : "Your business PAN will set this name exactly."} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField fullWidth size="small" required label="Address" disabled={!data.editable} value={form.address}
            onChange={(e) => set("address", e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth size="small" required label="City" disabled={!data.editable} value={form.city}
            onChange={(e) => set("city", e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth size="small" required label="State" disabled={!data.editable} value={form.state}
            onChange={(e) => set("state", e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth size="small" required label="PIN code" disabled={!data.editable} value={form.pincode}
            onChange={(e) => set("pincode", e.target.value)} slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 6 } }} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField fullWidth size="small" required label="Short name" disabled={!data.editable} value={form.shortname}
            onChange={(e) => set("shortname", e.target.value)}
            error={nameProblem !== ""}
            helperText={nameProblem !== "" ? `${nameProblem.charAt(0).toUpperCase()}${nameProblem.slice(1)}. Choose another.` : `${nameFree ? "✓ Available. " : ""}The name you are known by on AnkhOS. It must be unique, and it cannot be changed after you pay.`} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <RadioGroup value={hasDomain ? "yes" : "no"} onChange={(e) => chooseDomain(e.target.value === "yes")}>
            <FormControlLabel value="yes" disabled={!data.editable} control={<Radio size="small" />} label="I have a domain (recommended)" />
            <FormControlLabel value="no" disabled={!data.editable} control={<Radio size="small" />} label="I don't have a domain" />
          </RadioGroup>
        </Grid>
        {hasDomain ? (
          <Grid size={{ xs: 12 }}>
            <TextField fullWidth size="small" required label="Your domain" placeholder="supernet.in" disabled={!data.editable} value={form.domain}
              onChange={(e) => set("domain", e.target.value)} helperText="Use your main domain only, like ankhnet.io. Don't use a subdomain like www.ankhnet.io. We'll ask you to prove it with a DNS record." slotProps={{ htmlInput: { "data-testid": "domain" } }} />
          </Grid>
        ) : (
          <Grid size={{ xs: 12 }}>
            <Alert severity="warning" data-testid="no-domain-warning">
              <AlertTitle>Please read before you continue</AlertTitle>
              Without a domain of your own, you are registered under Ankh's domain. <b>This namespace stays with you permanently, even if you buy a
              domain later.</b> You can add a new domain to show on your portal and invoices, but this namespace will not change. A domain costs
              little and is easy to maintain, so we recommend buying one first.
            </Alert>
          </Grid>
        )}
        {form.shortname.trim() !== "" && (
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" sx={{ mb: 1 }} data-testid="namespace-preview">
              Your namespace: <NamespaceName>{preview}</NamespaceName>
            </Typography>
            <FormControlLabel disabled={!data.editable}
              control={<Checkbox size="small" checked={form.declared} onChange={(e) => set("declared", e.target.checked)} slotProps={{ input: { "data-testid": "declared" } as never }} />}
              label={<>I am registering the namespace <NamespaceName>{preview}</NamespaceName>. I understand this namespace will not change in future, even if I buy or change a domain later.</>} />
          </Grid>
        )}
      </Grid>
      {data.editable && (
        <Button sx={{ mt: 2 }} variant="outlined" onClick={onSave} disabled={nameProblem !== "" || save.isPending || (saved && !dirty)} data-testid="save-business">
          Save details
        </Button>
      )}

      {data.business.domain && !dirty && (
        <>
          <Typography variant="subtitle1" sx={{ mt: 3 }}>
            Prove your domain {data.domainverified && "✓"}
          </Typography>
          {data.domainverified ? (
            <Alert severity="success" sx={{ mt: 1 }} data-testid="domain-proved">
              {data.business.domain} is proved.
            </Alert>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">Add this TXT record at your domain provider, then press Verify:</Typography>
              <Box component="pre" sx={{ p: 1.5, my: 1, bgcolor: "action.hover", borderRadius: 1, overflowX: "auto", fontSize: 13 }} data-testid="txt-record">
                {`Name:  ${data.txtname}\nType:  TXT\nValue: ${data.txtvalue}`}
              </Box>
              <Button variant="outlined" onClick={() => verify.mutate()} disabled={verify.isPending} data-testid="verify-domain">
                Verify
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Can't do this now? Choose "I don't have a domain" above and save again.
              </Typography>
            </Box>
          )}
        </>
      )}

      {noFee ? (
        <>
          <Typography variant="subtitle1" sx={{ mt: 3 }}>
            Step 3: Online verification
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} data-testid="kyc-next">
            Free of charge. {verifyOpen ? "We check your PAN and bank account, then the owner's PAN and Aadhaar." : emailVerified ? "Save your details and prove your domain first." : "Verify your email first."}
          </Typography>
        </>
      ) : (
        <>
      <Typography variant="subtitle1" sx={{ mt: 3 }}>
        Step 3: Onboarding verification fee {paid && "✓"}
      </Typography>
      {paid ? (
        <Alert severity="success" sx={{ mt: 1 }} data-testid="fee-paid">
          {rupees(data.fee.amount)} paid. Your invoice will reach you by email.
        </Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {rupees(data.fee.amount)}, non-refundable. It covers your online verification.
          </Typography>
          {!emailVerified && (
            <Typography variant="body2" color="text.secondary">
              Verify your email first.
            </Typography>
          )}
          <Button sx={{ mt: 1 }} variant="contained" onClick={() => pay.mutate()}
            disabled={pay.isPending || !emailVerified || !saved || dirty || !domainReady} data-testid="pay-fee">
            Pay {rupees(data.fee.amount)}
          </Button>
        </>
      )}
        </>
      )}
      {verifyOpen && (noFee || paid) && <KycSteps />}
      {notice && (
        <Alert severity="info" sx={{ mt: 2 }} data-testid="onboarding-notice">
          {notice}
        </Alert>
      )}
    </Box>
  );
}
